<template>
  <div>
    <InventoryHeaderTab />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Product Details</h5>
        <p class="page-sub">View full product information and variants</p>
      </div>
      <div class="page-actions">
        <button type="button" @click="$router.go(-1)" class="btn-outline">
          <font-awesome-icon icon="arrow-left" /> Back
        </button>
        <router-link :to="$url(`/edit-product/${params.id}`)" class="btn-primary-action">
          <font-awesome-icon icon="pen" /> Edit Product
        </router-link>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="form-card mb-4">
      <div class="skeleton-grid">
        <div class="skeleton skeleton--field" v-for="n in 6" :key="n"></div>
      </div>
    </div>

    <template v-else>

      <!-- Product Info Card -->
      <div class="form-card mb-4">
        <div class="section-header">
          <div class="section-icon section-icon--blue">
            <font-awesome-icon icon="box" />
          </div>
          <div>
            <p class="section-title">Product Information</p>
            <p class="section-sub">Core details and pricing</p>
          </div>
        </div>

        <!-- Product Image -->
        <div class="product-image-row">
          <div v-if="theproduct?.image" class="product-image">
            <img :src="host.hostName + theproduct.image" alt="Product image" />
          </div>
          <div v-else class="product-image product-image--placeholder">
            <font-awesome-icon icon="box" />
          </div>
          <div>
            <p class="product-hero-name">{{ theproduct.product_name }}</p>
            <p class="product-hero-sub">{{ theproduct.bar_code ? 'Barcode: ' + theproduct.bar_code : 'No barcode' }}</p>
          </div>
        </div>

        <div class="section-divider"></div>

        <div class="detail-grid">
          <div class="detail-item">
            <span class="detail-label">Barcode</span>
            <span class="detail-value">{{ theproduct.bar_code || '—' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Unit of Measurement</span>
            <span class="detail-value">{{ theproduct.unit_of_measurement || '—' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Cost Price</span>
            <span class="detail-value price-value">{{ $filters.formatMoney(theproduct.cost_price) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Mark Up</span>
            <span class="detail-value">{{ theproduct.markup ? theproduct.markup + '%' : '—' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Selling Price</span>
            <span class="detail-value price-value price-value--selling">{{ $filters.formatMoney(theproduct.selling_price) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Warning Quantity</span>
            <span class="detail-value">
              <span v-if="theproduct.warning_quantity" class="warning-qty-badge">
                <font-awesome-icon icon="exclamation-triangle" class="me-1" />
                {{ theproduct.warning_quantity }}
              </span>
              <span v-else>—</span>
            </span>
          </div>
        </div>
      </div>

      <!-- Variants Card -->
      <div class="form-card">
        <div class="section-header">
          <div class="section-icon section-icon--teal">
            <font-awesome-icon icon="layer-group" />
          </div>
          <div>
            <p class="section-title">Price Variants</p>
            <p class="section-sub">{{ thevariants.length > 0 ? thevariants.length + ' variant(s) configured' : 'No variants configured' }}</p>
          </div>
        </div>

        <!-- No variants -->
        <div v-if="!thevariants.length" class="empty-variants">
          <font-awesome-icon icon="layer-group" class="empty-icon" />
          <p class="empty-text">No variants have been added to this product.</p>
          <router-link :to="$url(`/edit-product/${params.id}`)" class="btn-primary-action">
            <font-awesome-icon icon="plus" /> Add Variants
          </router-link>
        </div>

        <!-- Variants Table -->
        <div v-else class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Variant Name</th>
                <th>Price</th>
                <th>Quantity per Unit</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(variant, index) in thevariants" :key="variant.id">
                <td class="text-muted-cell">{{ index + 1 }}</td>
                <td>
                  <span class="variant-name-badge">{{ variant.variant_name }}</span>
                </td>
                <td class="price-value">{{ $filters.formatMoney(variant.variant_selling_price) }}</td>
                <td class="text-muted-cell">{{ variant.variant_quantity }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </template>
  </div>
</template>

<script>
import InventoryHeaderTab from './component/inventoryheadertab.vue';
import axios from 'axios';
import { useRoute } from 'vue-router';
import { ref } from '@vue/reactivity';
import { onMounted } from '@vue/runtime-core';
import host from '../../../main';

export default {
  components: { InventoryHeaderTab },

  setup() {
    const route      = useRoute();
    const theproduct = ref({});
    const thevariants = ref([]);
    const loading    = ref(false);
    // expose params so template can access route.params.id
    const params     = route.params;

    const GetProducts = async () => {
      loading.value = true;
      try {
        const res         = await axios.get(`/inventory/product/${params.id}`);
        theproduct.value  = res.data.data;
        thevariants.value = theproduct.value?.variants ?? [];
      } finally {
        loading.value = false;
      }
    };

    // Pass function reference, not invocation result
    onMounted(GetProducts);

    return { theproduct, thevariants, loading, GetProducts, host, params };
  },
};
</script>

<style scoped>
/* ── Page Header ── */
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }

/* ── Skeleton ── */
.skeleton-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:16px; padding:8px 0; }
.skeleton { border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
.skeleton--field { height:52px; }

/* ── Form Card ── */
.form-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
.mb-4      { margin-bottom:16px; }

/* ── Section Header ── */
.section-header { display:flex; align-items:center; gap:14px; margin-bottom:18px; }
.section-icon   { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
.section-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
.section-icon--teal { background:rgba(13,148,136,.1); color:#0d9488; }
.section-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
.section-sub   { font-size:12px; color:#7a90b0; margin:0; }

/* ── Divider ── */
.section-divider { height:1px; background:#f0f4f8; margin:18px 0; }

/* ── Product Image Row ── */
.product-image-row { display:flex; align-items:center; gap:16px; }
.product-image {
  width:72px; height:72px; border-radius:12px; overflow:hidden;
  border:1px solid #e4eaf2; flex-shrink:0;
}
.product-image img { width:100%; height:100%; object-fit:contain; }
.product-image--placeholder {
  background:rgba(0,74,173,.08); color:#004AAD;
  display:flex; align-items:center; justify-content:center; font-size:26px;
}
.product-hero-name { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 4px; }
.product-hero-sub  { font-size:13px; color:#7a90b0; margin:0; }

/* ── Detail Grid ── */
.detail-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:20px; }
.detail-item { display:flex; flex-direction:column; gap:6px; }
.detail-label { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
.detail-value { font-size:14px; color:#1a2b45; font-weight:500; }
.price-value  { font-weight:700; color:#1a2b45; }
.price-value--selling { color:#004AAD; font-size:15px; }

/* ── Warning Qty Badge ── */
.warning-qty-badge { display:inline-flex; align-items:center; background:#fff7ed; color:#e08c00; border:1px solid #fed7aa; border-radius:6px; padding:3px 10px; font-size:13px; font-weight:600; }

/* ── Variants Table ── */
.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:11px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 16px; font-size:13.5px; vertical-align:middle; }
.text-muted-cell { color:#4a5e78; }

/* ── Variant Name Badge ── */
.variant-name-badge { display:inline-block; background:#edf4ff; color:#004AAD; border:1px solid #c5d6f5; border-radius:6px; padding:3px 10px; font-size:13px; font-weight:600; }

/* ── Empty Variants ── */
.empty-variants { display:flex; flex-direction:column; align-items:center; padding:36px 20px; text-align:center; }
.empty-icon { font-size:32px; color:#dde5f0; margin-bottom:12px; }
.empty-text { font-size:13.5px; color:#7a90b0; margin:0 0 14px; }

/* ── Buttons ── */
.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; text-decoration:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-primary-action:hover { background:#003b8e; color:#fff; text-decoration:none; }
.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; text-decoration:none; }
.btn-outline:hover { background:#f4f7fb; color:#1a2b45; text-decoration:none; }

/* ── Responsive ── */
@media(max-width:768px) {
  .detail-grid  { grid-template-columns:repeat(2,1fr); }
  .skeleton-grid { grid-template-columns:1fr; }
  .form-card    { padding:16px; }
  .page-header,.page-actions { flex-direction:column; align-items:stretch; }
  .btn-primary-action,.btn-outline { width:100%; justify-content:center; }
}
@media(max-width:480px) { .detail-grid { grid-template-columns:1fr; } }
</style>